import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppLogo from '../../components/AppLogo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RequestItemGroups from '../../components/request-builder/RequestItemGroups';
import RequestStatusBadge, { getRequestStatusConfig } from '../../components/request-builder/RequestStatusBadge';
import { formatRequestAmount, normalizeRequestStatus } from '../../components/request-builder/requestBuilderUtils';
import repositoryContainer from '../../services/RepositoryContainer';
import PaymentSummary from '../../components/payments/PaymentSummary';
import ConversationButton from '../../components/chat/ConversationButton';
import ConversationModal from '../../components/chat/ConversationModal';
import { getChatAccessToken, hasConversationNote } from '../../utils/chatAccess';
import { needsPriceFinalization } from '../../utils/pricingUtils';

const STATUS_MESSAGES = {
  pending: 'Your request has been received and is waiting for review.',
  inprogress: 'The business is currently reviewing or working on your request.',
  completed: 'Your request has been completed.',
  rejected: 'The business could not accept this request. Review their response below for details.',
  cancelled: 'This request has been cancelled.',
};

const formatDate = (value, options = { dateStyle: 'medium' }) => {
  if (!value) return 'Not provided';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-GH', options).format(date);
};

const TrackingSkeleton = () => (
  <div className="mx-auto max-w-3xl animate-pulse space-y-5">
    <div className="h-44 rounded-2xl bg-muted" />
    <div className="h-72 rounded-2xl bg-muted" />
    <div className="h-36 rounded-2xl bg-muted" />
  </div>
);

const RequestTrackingPage = () => {
  const navigate = useNavigate();
  const { referenceCode = '' } = useParams();
  const decodedReference = useMemo(() => {
    try {
      return decodeURIComponent(referenceCode).trim();
    } catch {
      return referenceCode.trim();
    }
  }, [referenceCode]);
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(decodedReference));
  const [error, setError] = useState('');
  const [isStartingPayment, setIsStartingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isConversationOpen, setIsConversationOpen] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    if (!decodedReference) {
      setIsLoading(false);
      setError('A request reference code is required.');
      return () => { isCurrent = false; };
    }

    const loadRequest = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await repositoryContainer.bookingRequest.getByReferenceCode(decodedReference);
        if (isCurrent) setRequest(data);
      } catch (loadError) {
        if (!isCurrent) return;
        setError(loadError?.status === 404
          ? 'No request was found with this reference code.'
          : loadError?.data?.message || 'We could not load this request. Please try again.');
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadRequest();
    return () => { isCurrent = false; };
  }, [decodedReference]);

  const items = useMemo(() => (request?.items || []).map((item) => ({
    ...item,
    id: item.serviceItemId,
    price: Number(item.unitPrice) || 0,
    quantity: Number(item.quantity) || 1,
    type: item.type?.toString().toLowerCase() || 'item',
  })), [request?.items]);
  const normalizedStatus = normalizeRequestStatus(request?.status);
  const statusConfig = getRequestStatusConfig(request?.status);
  const hasRequestedSchedule = Boolean(request?.requestedDate && request?.requestedTime);
  const requestNeedsFinalPricing = needsPriceFinalization(request);
  const canPay = Number(request?.balanceDue) > 0
    && !['rejected', 'cancelled'].includes(normalizedStatus)
    && Boolean(request?.id)
    && !requestNeedsFinalPricing;
  const chatAccessToken = request?.referenceCode
    ? getChatAccessToken('bookingRequest', request.referenceCode)
    : '';

  const handlePayment = async () => {
    if (!request?.id || isStartingPayment || requestNeedsFinalPricing) return;
    setIsStartingPayment(true);
    setPaymentError('');
    try {
      const callbackUrl = `${window.location.origin}/request-callback`;
      const raw = await repositoryContainer.bookingRequest.initiatePayment(request.id, callbackUrl);
      const payment = raw?.response ?? raw;
      if (!payment?.authorizationUrl || !payment?.reference) {
        throw new Error('The payment provider did not return a checkout link.');
      }
      localStorage.setItem('clawfe:pendingRequest:global', JSON.stringify({
        requestId: request.id,
        referenceCode: payment.sourceReferenceCode || request.referenceCode,
        clientEmail: request.clientEmail,
        paymentReference: payment.reference,
      }));
      window.location.assign(payment.authorizationUrl);
    } catch (paymentStartError) {
      setPaymentError(paymentStartError?.data?.message || paymentStartError?.message || 'Payment could not be started.');
      setIsStartingPayment(false);
    }
  };
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/discover');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>{decodedReference ? `Track ${decodedReference}` : 'Track Request'} — Claw</title>
      </Helmet>

      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 md:px-6">
          <Link to="/discover" aria-label="Go to business discovery">
            <AppLogo variant="wordmark" size={110} shape="none" />
          </Link>
          <span className="text-sm font-medium text-muted-foreground">Request Tracking</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-12">
        <button
          type="button"
          onClick={handleBack}
          className="mb-5 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Icon name="ArrowLeft" size={17} />
          Back
        </button>
        {isLoading ? (
          <TrackingSkeleton />
        ) : error ? (
          <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <Icon name="SearchX" size={28} />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold">Request not available</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <Button asChild className="mt-6"><Link to="/discover">Find a Business</Link></Button>
          </div>
        ) : request ? (
          <div className="mx-auto max-w-3xl space-y-5">
            <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
              <div className={`border-b p-5 md:p-7 ${statusConfig.className}`}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-80">Request reference</p>
                    <h1 className="mt-1 font-mono text-xl font-bold md:text-2xl">{request.referenceCode}</h1>
                  </div>
                  <RequestStatusBadge status={request.status} className="bg-card/70" />
                </div>
                <p className="mt-5 text-sm font-medium">
                  {STATUS_MESSAGES[normalizedStatus] || 'The latest request status is shown above.'}
                </p>
              </div>

              <div className={`grid gap-4 p-5 md:p-7 ${hasRequestedSchedule ? 'sm:grid-cols-2' : ''}`}>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Customer</p>
                  <p className="mt-1 font-medium">{request.clientName}</p>
                  <p className="text-sm text-muted-foreground">{request.clientEmail}</p>
                  <p className="text-sm text-muted-foreground">{request.clientPhone}</p>
                </div>
                {hasRequestedSchedule && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred schedule</p>
                    <p className="mt-1 font-medium">{formatDate(request.requestedDate)}</p>
                    <p className="text-sm text-muted-foreground">{request.requestedTime}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Schedule remains subject to business confirmation.</p>
                  </div>
                )}
              </div>
            </section>

            {request.companyNote && (
              <section className="rounded-2xl border border-primary/30 bg-primary/5 p-5 md:p-6">
                <div className="flex items-center gap-2 text-primary">
                  <Icon name="MessageSquareText" size={18} />
                  <h2 className="font-heading text-lg font-semibold">Business response</h2>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{request.companyNote}</p>
                {chatAccessToken && (
                  <ConversationButton
                    hasNote
                    onClick={() => setIsConversationOpen(true)}
                    className="mt-5 h-11 bg-card"
                  />
                )}
              </section>
            )}

            <section className="rounded-2xl border border-border bg-card p-5 shadow-md md:p-7">
              <h2 className="mb-4 font-heading text-xl font-bold">Payment</h2>
              {requestNeedsFinalPricing ? (
                <div className="rounded-xl border border-warning/20 bg-warning/10 p-4">
                  <p className="font-medium text-foreground">Awaiting final price</p>
                  <p className="mt-1 text-sm text-muted-foreground">The business must confirm every price-range service before payment can begin.</p>
                </div>
              ) : (
                <PaymentSummary record={request} totalAmount={request.totalAmount} />
              )}
              {canPay && (
                <Button
                  type="button"
                  fullWidth
                  className="mt-5"
                  iconName="CreditCard"
                  loading={isStartingPayment}
                  onClick={handlePayment}
                >
                  {Number(request.amountPaid) > 0 ? 'Pay Remaining Balance' : 'Pay Now'}
                </Button>
              )}
              {paymentError && <p className="mt-3 text-sm text-error">{paymentError}</p>}
            </section>

            <section className="rounded-2xl border border-border bg-card p-5 shadow-md md:p-7">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="font-heading text-xl font-bold">Request items</h2>
                <span className="font-semibold text-primary">
                  {requestNeedsFinalPricing ? 'From ' : ''}{formatRequestAmount(request.totalAmount)}
                </span>
              </div>
              {items.length > 0 ? <RequestItemGroups items={items} showImages /> : <p className="text-sm text-muted-foreground">Item details are not available.</p>}
              {request.note && (
                <div className="mt-5 border-t border-border pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Customer note</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm">{request.note}</p>
                </div>
              )}
            </section>

            {chatAccessToken && !request.companyNote && (
              <ConversationButton
                hasNote={hasConversationNote(request.note)}
                onClick={() => setIsConversationOpen(true)}
                className="h-11 bg-card"
              />
            )}

            {request.history?.length > 0 && (
              <section className="rounded-2xl border border-border bg-card p-5 shadow-md md:p-7">
                <h2 className="font-heading text-xl font-bold">Activity</h2>
                <ol className="mt-4 space-y-4 border-l-2 border-border pl-5">
                  {request.history.map((entry, index) => (
                    <li key={`${entry.timestamp}-${index}`} className="relative">
                      <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-card bg-primary" />
                      <p className="text-sm font-medium">{entry.action}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDate(entry.timestamp, { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        ) : null}
      </main>

      <ConversationModal
        isOpen={isConversationOpen}
        onClose={() => setIsConversationOpen(false)}
        audience="customer"
        subjectType="bookingRequest"
        subjectId={request?.id}
        referenceCode={request?.referenceCode || decodedReference}
        chatAccessToken={chatAccessToken}
      />
    </div>
  );
};

export default RequestTrackingPage;
